<script lang="ts" setup>
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import Image from '@/assets/show/image.webp'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { currentPriceFormat, formatPrice } from '@/helpers/current-price'
import { findTicketLot } from '@/helpers/ticket-batch'
import { cn } from '@/lib/utils'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}

defineProps<{
    order: OrderWithCoupon
}>()
</script>

<template>
    <Card :class="cn('font-inter sticky top-0 h-fit w-full max-w-96 border-slate-200 bg-white')">
        <CardHeader>
            <CardTitle class="text-lg font-medium">
                {{ $t('Order summary') }}
            </CardTitle>
        </CardHeader>
        <CardContent class="flex flex-col gap-x-4 gap-y-6">
            <div v-for="item in order.items" :key="item.id" class="flex gap-4 border-b pb-4">
                <img :src="Image" alt="Ingresso" class="h-[78px] w-[72px] rounded-md object-cover" />
                <div class="flex w-full flex-col gap-1">
                    <p class="text-base font-normal">{{ $t('Ticket type') }}</p>
                    <div class="flex items-center justify-between gap-2">
                        <p class="font-bold">{{ $t(item.ticket.category) }}</p>
                        <p>{{ item.quantity }}x</p>
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <p class="text-sm">{{ $t('Batch') }} {{ findTicketLot(item.ticket.promotions) + 1 }}</p>
                        <p class="font-bold">
                            {{ currentPriceFormat(item.ticket) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="flex w-full flex-col gap-[8px] border-b pb-6">
                <div class="flex w-full items-center justify-between">
                    <p class="text-sm text-slate-600">{{ $t('Subtotal') }}:</p>
                    <p class="text-sm font-semibold text-slate-600">{{ formatPrice(order.order_snap_shot.subtotal_cents / 100) }}</p>
                </div>
                <div class="flex w-full items-center justify-between" v-if="order.order_snap_shot.order_discount_cents">
                    <p class="text-sm text-slate-600">{{ $t('Discounts') }}:</p>
                    <p class="text-sm font-semibold text-green-600">10% (-{{ formatPrice(order.order_snap_shot.order_discount_cents / 100) }})</p>
                </div>
                <div class="flex w-full items-center justify-between" v-if="order.coupon">
                    <p class="text-sm text-slate-600">{{ $t('Coupon') }}:</p>
                    <p class="text-sm font-semibold text-green-600" v-if="order.coupon.category == 'currency'">
                        ( - {{ formatPrice(order.coupon.value_cents / 100) }})
                    </p>

                    <p class="text-sm font-semibold text-green-600" v-if="order.coupon.category == 'percentage'">
                        {{ order.coupon.percentage_value }}% (-
                        {{ formatPrice((order.order_snap_shot.subtotal_cents / 100) * (order.coupon.percentage_value / 100)) }})
                    </p>
                </div>
            </div>
            <!-- <div v-if="order.transaction_detail && order.transaction_detail.payment_type === 'CREDIT_CARD'" class="flex flex-col gap-6">
                <div class="flex justify-between text-sm">
                    <p class="font-semibold text-slate-800">{{ $t('You will pay') }}:</p>
                    <p class="font-semibold text-slate-800">
                        {{ order.transaction_detail.installments }}x
                        {{ formatPrice(order.transaction_detail.amount / 100 / order.transaction_detail.installments) }}
                    </p>
                </div>
            </div> -->
            <div class="flex flex-col gap-6">
                <div class="flex justify-between">
                    <p class="text-lg font-semibold text-slate-800">{{ $t('Total') }}:</p>

                    <p class="text-lg font-semibold text-slate-800">
                        {{ formatPrice((order.transaction_detail?.amount || order.order_snap_shot.total_cents) / 100) }}
                    </p>
                </div>
            </div>
        </CardContent>
    </Card>
</template>
